import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const ROLES = [
    { value: "", label: "All" },
    { value: "customer", label: "Customers" },
    { value: "admin", label: "Admins" },
    { value: "pharmacist", label: "Pharmacists" },
];
const ROLE_PILL = {
    admin: "bg-brand-100 text-brand-700",
    pharmacist: "bg-amber-100 text-amber-700",
    customer: "bg-slate-100 text-slate-600",
};

const Content = () => {
    const [params, setParams] = useSearchParams();
    const [role, setRole] = useState(params.get("role") || "");
    const [q, setQ] = useState("");
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/users", { params: { role: role || undefined, q: q.trim() || undefined, page, page_size: 25 } });
            setRows(r.data.items); setTotal(r.data.total);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [role, page]);

    return (
        <div className="space-y-5" data-testid="admin-users-page">
            <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
                    {ROLES.map((r) => (
                        <button key={r.value} onClick={() => { setRole(r.value); setPage(1); const n = new URLSearchParams(params); if (r.value) n.set("role", r.value); else n.delete("role"); setParams(n, { replace: true }); }}
                            className={`rounded-md px-3 py-1.5 text-xs font-medium ${role === r.value ? "bg-brand-700 text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`users-role-${r.value || "all"}`}>{r.label}</button>
                    ))}
                </div>
                <form onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }} className="relative ml-auto flex-1 max-w-md">
                    <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or phone" className="input-clean !rounded-lg pl-10" data-testid="users-search" />
                </form>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-soft">
                <table className="min-w-full divide-y divide-slate-100 text-sm">
                    <thead className="bg-slate-50/80 text-[10px] uppercase tracking-wider text-slate-500">
                        <tr><th className="px-4 py-3 text-left">User</th><th className="px-4 py-3 text-left">Role</th><th className="px-4 py-3 text-right">Addresses</th><th className="px-4 py-3 text-left">Joined</th><th /></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? <tr><td colSpan={5} className="px-4 py-6"><div className="h-6 skeleton rounded" /></td></tr> :
                          rows.length === 0 ? <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No users match.</td></tr> :
                          rows.map((u) => (
                            <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                                <td className="px-4 py-3">
                                    <Link to={`/admin/users/${u.id}`} className="font-medium text-slate-900 hover:text-brand-700">{u.full_name}</Link>
                                    <p className="text-xs text-slate-500">{u.email} · {u.phone}</p>
                                    {u.disabled && <span className="mt-1 inline-block rounded bg-rose-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-rose-600">Disabled</span>}
                                </td>
                                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${ROLE_PILL[u.role]}`}>{u.role}</span></td>
                                <td className="px-4 py-3 text-right tabular-nums">{u.addresses_count}</td>
                                <td className="px-4 py-3 text-xs text-slate-500">{u.created_at && new Date(u.created_at).toLocaleDateString("en-IN")}</td>
                                <td className="px-4 py-3 text-right">
                                    <Link to={`/admin/users/${u.id}`} className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 hover:border-brand-300 hover:text-brand-700">Open</Link>
                                </td>
                            </tr>
                          ))}
                    </tbody>
                </table>
                {!loading && total > 25 && (
                    <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
                        <span>Page {page} of {Math.ceil(total / 25)}</span>
                        <div className="flex gap-2">
                            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="rounded-md border border-slate-200 px-3 py-1 disabled:opacity-50">Prev</button>
                            <button disabled={page * 25 >= total} onClick={() => setPage(p => p + 1)} className="rounded-md border border-slate-200 px-3 py-1 disabled:opacity-50">Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const AdminUsers = () => (
    <AdminAuthGuard requireAdmin>
        <AdminLayout title="Users & roles" subtitle="Manage customers, admins and pharmacists">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminUsers;
